import { gql, useLazyQuery, useMutation } from '@apollo/react-hooks';
import { Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import * as yup from 'yup';
import { ContentEditor, Header, ModalThatSlides } from '../../components';
import { Block, Button, SelectInput, Text, TextInput } from '../../components/layout';
import { SvgIconArrowLeft, SvgIconSettings } from '../../svgs';
import toast from '../../utils/toast';
import validateFormSchema from '../../utils/validateFormSchema';

function PageEdit() {
    const history = useHistory();
    let { pageId } = useParams();
    const [result, setResult] = useState(null);
    const [isModalMetatagsOpen, setIsModalMetatagsOpen] = useState(false);

    let submitForm = null;

    const [queryPage, { loading }] = useLazyQuery(
        gql`
            query page($id: ID!) {
                page(id: $id) {
                    id
                    slug
                    title
                    content
                    status
                    metatags
                    template {
                        id
                        structure
                    }
                }
            }
        `,
        {
            onError: () => { },
            onCompleted: (data) => {
                if (data && data.page) {
                    setResult(data.page);
                }
            }
        }
    );

    const [mutationPageUpdate, { loading: loadingPageUpdate }] = useMutation(
        gql`
            mutation pageUpdate($id: ID!, $content: Json) {
                pageUpdate(id: $id, content: $content) {
                    id
                    content
                }
            }
        `,
        {
            onError: () => { },
            onCompleted: (data) => {
                if (data && data.pageUpdate) {
                    setResult({ ...result, ...data.pageUpdate });
                    toast.success(`Saved`);
                }
            }
        }
    );

    const [mutationPageUpdateGeneric, { loading: loadingPageUpdateGeneric }] = useMutation(
        gql`
            mutation pageUpdate($id: ID!, $title: String, $slug: String, $status: PageStatus) {
                pageUpdate(id: $id, title: $title, slug: $slug, status: $status) {
                    id
                    title
                    slug
                    status
                }
            }
        `,
        {
            onError: () => { },
            onCompleted: (data) => {
                if (data && data.pageUpdate) {
                    setResult({ ...result, ...data.pageUpdate });
                }
            }
        }
    );

    const [mutationPageUpdateMetatags, { loading: loadingPageUpdateMetatags }] = useMutation(
        gql`
            mutation pageUpdate($id: ID!, $metatags: Json) {
                pageUpdate(id: $id, metatags: $metatags) {
                    id
                    metatags
                }
            }
        `,
        {
            onError: () => { },
            onCompleted: (data) => {
                if (data && data.pageUpdate) {
                    setResult({ ...result, ...data.pageUpdate });
                }
            }
        }
    );

    useEffect(() => {
        queryPage({ variables: { id: pageId } });
    }, []);

    return (
        <>
            <Block flex height="100%">
                <Header
                    leftChildren={
                        <Block height="100%" row>
                            <Block width={52} height={52} center middle pointer onClick={() => history.push("/dashboard/pages")}>
                                <SvgIconArrowLeft />
                            </Block>
                            <Block width={1} height="100%" color="greyAlmostWhite" />
                            <Block width={229} height="100%" middle>
                                {result && <Text size={18} height={32} ml={24}>{result.title}</Text>}
                            </Block>
                            <Block width={1} height="100%" color="greyAlmostWhite" />
                        </Block>
                    }
                    rightChildren={
                        <Block row>
                            <Block pointer row middle center pl={48} pr={48} color="greyLightest" onClick={() => setIsModalMetatagsOpen(true)}>
                                <SvgIconSettings />
                                <Text size={14} height={28} ml={8}>Settings</Text>
                            </Block>
                            <Button style={{ minWidth: 309 }} text="Save" onClick={() => submitForm()} loading={loadingPageUpdate} />
                        </Block>
                    }
                />
                <Block flex pl={230} pt={64} pb={64} style={{ overflow: "auto" }}>
                    {result && <ContentEditor
                        onSubmit={(values) => mutationPageUpdate({
                            variables: {
                                id: pageId,
                                content: values
                            }
                        })}
                        bindSubmitForm={(method) => submitForm = method}
                        values={result.content}
                        structure={result.template.structure}
                    />}
                </Block>
            </Block>
            <ModalThatSlides title="Manage Settings" width="820px" isOpen={isModalMetatagsOpen} onRequestClose={() => setIsModalMetatagsOpen(false)}>
                <Block>
                    <Formik
                        enableReinitialize
                        initialValues={result}
                        validateOnChange={false}
                        validateOnBlur={false}
                        validate={values => {
                            return validateFormSchema(
                                yup.object().shape({
                                    title: yup.string().required(),
                                    slug: yup.string().required(),
                                    status: yup.string().required()
                                }),
                                values
                            )
                        }}
                        onSubmit={(values, { setSubmitting }) => {
                            setSubmitting(false);
                            mutationPageUpdateGeneric({
                                variables: {
                                    id: pageId,
                                    title: values.title,
                                    slug: values.slug,
                                    status: values.status
                                }
                            })
                        }}
                    >
                        {({
                            values,
                            errors,
                            handleChange,
                            handleBlur,
                            handleSubmit,
                        }) => {
                            return (
                                <form onSubmit={handleSubmit} autoComplete="off">
                                    <Block flex color="white" pt={16} pb={16} pl={24} pr={24}>
                                        <Text size={14} height={20} mb={4}>Generic</Text>
                                        <Block mt={12}>
                                            <TextInput label="Title" name="title" onChange={handleChange} onBlur={handleBlur} value={values.title} error={errors.title} />
                                        </Block>
                                        <Block mt={12}>
                                            <TextInput label="Slug" name="slug" onChange={handleChange} onBlur={handleBlur} value={values.slug} error={errors.slug} />
                                        </Block>
                                        <Block mt={12}>
                                            <SelectInput label="Status" name="status" onBlur={handleBlur} value={values.status} error={errors.status} options={[{ label: "Published", value: "PUBLISHED" }, { label: "Draft", value: "DRAFT" }]} />
                                        </Block>
                                        <Block mt={28}>
                                            <Button type="submit" text="Save" loading={loadingPageUpdateGeneric} />
                                        </Block>
                                    </Block>
                                </form>
                            )
                        }}
                    </Formik>
                </Block>
                <Block mt={12}>
                    <Formik
                        initialValues={(result && result.metatags) || {}}
                        validateOnChange={false}
                        validateOnBlur={false}
                        validate={values => {
                            const errors = {};
                            return errors;
                        }}
                        onSubmit={(values, { setSubmitting }) => {
                            setSubmitting(false);
                            mutationPageUpdateMetatags({
                                variables: {
                                    id: pageId,
                                    metatags: values
                                }
                            })
                        }}
                    >
                        {({
                            values,
                            errors,
                            handleChange,
                            handleBlur,
                            handleSubmit,
                        }) => {
                            return (
                                <form onSubmit={handleSubmit} autoComplete="off">
                                    <Block flex color="white" pt={16} pb={16} pl={24} pr={24}>
                                        <Text size={14} height={20} mb={4}>Metatags</Text>
                                        <Block mt={12}>
                                            <TextInput label="Title" name="title" onChange={handleChange} onBlur={handleBlur} value={values.title} error={errors.title} />
                                        </Block>
                                        <Block mt={12}>
                                            <TextInput label="Description" name="description" onChange={handleChange} onBlur={handleBlur} value={values.description} error={errors.description} />
                                        </Block>
                                        <Block mt={12}>
                                            <SelectInput label="Keywords" name="keywords" onBlur={handleBlur} value={values.keywords} error={errors.keywords} isMulti />
                                        </Block>
                                        <Block mt={28}>
                                            <Button type="submit" text="Save" loading={loadingPageUpdateMetatags} />
                                        </Block>
                                    </Block>
                                </form>
                            )
                        }}
                    </Formik>
                </Block>
            </ModalThatSlides>
        </>
    );
}

export default PageEdit;